import {
  // eslint-disable-next-line no-restricted-imports -- File defines overrides apollo hooks
  LazyQueryHookOptions as ApolloLazyQueryHookOptions, // eslint-disable-next-line no-restricted-imports -- File defines overrides apollo hooks
  MutationHookOptions as ApolloMutationHookOptions, // eslint-disable-next-line no-restricted-imports -- File defines overrides apollo hooks
  QueryHookOptions as ApolloQueryHookOptions,
  DefaultContext,
  OperationVariables,
  TypedDocumentNode, // eslint-disable-next-line no-restricted-imports -- File defines overrides apollo hooks
  useLazyQuery as useLazyQueryFromApollo, // eslint-disable-next-line no-restricted-imports -- File defines overrides apollo hooks
  useMutation as useMutationFromApollo, // eslint-disable-next-line no-restricted-imports -- File defines overrides apollo hooks
  useQuery as useQueryFromApollo,
} from '@apollo/client';
import { useContextualData } from '@opendoor/observability';

import { useAuthAuthenticated } from 'platform/auth-fe';
import { useReauth } from 'platform/components/ReauthContext';
import navigationHelpers from 'platform/navigationHelpers';
import { TEAM } from 'platform/ODTeamBoundary';

interface ApolloContext extends DefaultContext {
  linkContext?: { team: TEAM };
}

export interface QueryHookOptions<TData extends object, TVariables = OperationVariables>
  extends Omit<ApolloQueryHookOptions<TData, TVariables>, 'context'> {
  context?: ApolloContext;
}

export interface LazyQueryHookOptions<TData extends object, TVariables = OperationVariables>
  extends Omit<ApolloLazyQueryHookOptions<TData, TVariables>, 'context'> {
  context?: ApolloContext;
}

export type MutationHookOptions<
  TData extends object,
  TVariables = OperationVariables,
> = ApolloMutationHookOptions<TData, TVariables, ApolloContext>;

/**
 * Inject contextual data from Observability into Apollo's context.
 * Apollo re-executes queries when context values change, so we should only use static values here
 */
function useApolloContext<TData extends object, TVariables = OperationVariables>(
  options:
    | MutationHookOptions<TData, TVariables>
    | QueryHookOptions<TData, TVariables>
    | LazyQueryHookOptions<TData, TVariables>,
): ApolloContext {
  const observabilityContext = useContextualData();

  return {
    linkContext: {
      team: observabilityContext.data.team as TEAM,
      ...options?.context?.linkContext,
    },
    ...options?.context,
  };
}

/**
 * Wrap Apollo's `useQuery` to inject contextual data from Observability
 * Explicitly requires specifying the data type to avoid accidentally using the default `unknown`
 */
export function useQuery<TData extends object, TVariables = OperationVariables>(
  query: TypedDocumentNode<TData, TVariables>,
  options: QueryHookOptions<TData, TVariables> = {},
) {
  const context = useApolloContext(options);
  return useQueryFromApollo<TData, TVariables>(query, { ...options, context });
}

/**
 * Wrap Apollo's `useQuery` with an auth check
 * Explicitly requires specifying the data type to avoid accidentally using the default `unknown`
 */
export function useAuthenticatedQuery<TData extends object, TVariables = OperationVariables>(
  query: TypedDocumentNode<TData, TVariables>,
  options: QueryHookOptions<TData, TVariables> = {},
) {
  const authentication = useAuthAuthenticated(query.loc?.source.name ?? 'UnknownQuery');
  const { showReauth } = useReauth();

  return useQuery<TData, TVariables>(query, {
    ...options,
    skip: authentication?.state !== 'authenticated' || options.skip,
    onError: (error) => {
      if (
        authentication?.state === 'authenticated' &&
        error.message.includes(`"code":"reauthentication-required"`)
      ) {
        showReauth({
          title: 'Please sign in again to continue',
          onSuccess: () => navigationHelpers.hardPageReload(),
          onCancel: () => authentication.logout(),
        });
      }
      options.onError && options.onError(error);
    },
  });
}

/**
 * Wrap Apollo's `useMutation` to inject contextual data from Observability
 * Explicitly requires specifying the data type to avoid accidentally using the default `unknown`
 */
export function useMutation<TData extends object, TVariables = OperationVariables>(
  mutation: TypedDocumentNode<TData, TVariables>,
  options: MutationHookOptions<TData, TVariables> = {},
) {
  const context = useApolloContext(options);
  return useMutationFromApollo<TData, TVariables, ApolloContext>(mutation, { ...options, context });
}

/**
 * Wrap Apollo's `useLazyQuery` to inject contextual data from Observability
 * Explicitly requires specifying the data type to avoid accidentally using the default `unknown`
 */
export function useLazyQuery<TData extends object, TVariables = OperationVariables>(
  query: TypedDocumentNode<TData, TVariables>,
  options: LazyQueryHookOptions<TData, TVariables> = {},
) {
  const context = useApolloContext(options);
  return useLazyQueryFromApollo<TData, TVariables>(query, { ...options, context });
}
